// currently we don't have common select, so it is going to be changed in future design

import { useDirection } from '@radix-ui/react-direction'
import * as SelectPrimitive from '@radix-ui/react-select'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'
import { ButtonHTMLAttributes, HTMLProps } from 'react'

import { Check } from '@/components-latest/primitives/Icons'
import Text, { textVariants } from '@/components-latest/primitives/Text'

import ArrowDownSolid from '../../Icons/ArrowDownSolid'
import { inputVariants } from '../Input'
import Styles from './Select.module.scss'

const Select = (
  props: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
) => {
  const direction = useDirection()
  return <SelectPrimitive.Root dir={direction} {...props} />
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, children, ...props }, ref) => (
  <Text
    variant="nextSubtitle"
    className={clsx(Styles.value, className)}
    asChild>
    <span>
      <SelectPrimitive.Value ref={ref} {...props}>
        {children}
      </SelectPrimitive.Value>
    </span>
  </Text>
))
SelectValue.displayName = SelectPrimitive.Value.displayName

type ISelectTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> & {
  arrowVariant?: 'default' | 'solidArrow'
  variant?: 'default' | 'input'
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  ISelectTriggerProps
>(
  (
    {
      variant = 'default',
      arrowVariant = 'default',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const icon =
      arrowVariant === 'default' ? (
        <ChevronDown className={clsx(Styles.arrow)} />
      ) : (
        <ArrowDownSolid className={clsx(Styles.arrow, Styles.solidArrow)} />
      )

    return (
      <SelectPrimitive.Trigger
        ref={ref}
        className={clsx(
          variant === 'default'
            ? Styles.trigger
            : inputVariants({ intent: 'default' }),
          className
        )}
        {...props}>
        {children}
        <SelectPrimitive.Icon asChild>{icon}</SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    )
  }
)

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={clsx(Styles.content, className)}
      position={position}
      {...props}>
      <SelectPrimitive.Viewport className={clsx(Styles.viewport)}>
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={clsx(Styles.label, className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    textClassName?: string
  }
>(({ className, textClassName, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={clsx(Styles.item, className)}
    {...props}>
    <span
      className={clsx(
        textVariants({ variant: 'nextBody' }),
        Styles.itemText,
        textClassName
      )}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </span>

    <SelectPrimitive.ItemIndicator className={Styles.indicator}>
      <Check />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectAction = React.forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> & { value: string }
>(({ className, children, value, ...props }, ref) => (
  <button ref={ref} className={clsx(Styles.item, className)} {...props}>
    <Text
      variant="nextBody"
      className={clsx(Styles.itemText, className)}
      asChild>
      <span>{children}</span>
    </Text>

    <SelectPrimitive.Item value={value}>
      <SelectPrimitive.ItemIndicator className={Styles.indicator}>
        <Check />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  </button>
))
SelectAction.displayName = 'SelectAction'

const EmptyMessage: React.FC<HTMLProps<HTMLDivElement>> = ({
  className,
  children,
  ...props
}) => (
  <div className={clsx(Styles.emptyMessage, className)} {...props}>
    <span
      className={clsx(textVariants({ variant: 'nextBody' }), Styles.itemText)}>
      {children}
    </span>
  </div>
)
EmptyMessage.displayName = 'EmptyMessage'

export {
  EmptyMessage,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
}
